import type { IUser } from "types/user.types";

/**
 * Mirror of `equility-api`'s `AdminScope` enum
 * (`src/common/admin-scope/admin-scope.enum.ts`). The API is the source of
 * truth; this copy exists only so the nav can decide what to render.
 *
 * These strings are a de facto contract between two repos with no shared
 * package. Keep the two lists identical, and never rename a value once it has
 * been granted — the grants live as raw strings in `users.admin_scopes`, so a
 * rename is a data migration, not a refactor.
 *
 * ⚠️ THIS IS NAV GATING, NOT SECURITY. Hiding a menu entry hides nothing: the
 * routes in `Routes.tsx` are registered unconditionally, so anyone can still
 * type the URL. The real enforcement is `AdminGuard` on the API, which 403s
 * every call regardless of what this file says. The point here is that a
 * scoped hire sees a console containing only what they can actually use,
 * instead of a full menu of pages that render and then fail.
 */
export enum AdminScope {
	SUPPORT_TICKETS = "SUPPORT_TICKETS",
	SUPPORT_SESSIONS = "SUPPORT_SESSIONS",
	SUPPORT_ACCESS_OVERRIDE = "SUPPORT_ACCESS_OVERRIDE",
	AI_ANALYTICS = "AI_ANALYTICS",
	AI_CONFIG = "AI_CONFIG",
	USERS_READ = "USERS_READ",
	USERS_WRITE = "USERS_WRITE",
	STAFF_ACCESS_WRITE = "STAFF_ACCESS_WRITE",
	COMPANIES_READ = "COMPANIES_READ",
	COMPANIES_EXPORT = "COMPANIES_EXPORT",
	FILES = "FILES",
	BILLING = "BILLING",
	MEMBERSHIPS = "MEMBERSHIPS",
	RECONCILIATION_OPS = "RECONCILIATION_OPS",
	PLATFORM_DIAGNOSTICS = "PLATFORM_DIAGNOSTICS",
}

/**
 * Does this user hold `scope`?
 *
 * `admin: true` short-circuits to true, which keeps founder accounts seeing the
 * full console. Correct for nav, and NOT correct for a scope granted after the
 * legacy-admin backfill — see `hasGrantedScope` below.
 *
 * `adminScopes` arrives on `GET /user`, whose response is built from an
 * explicit `select` whitelist in `user.service.ts`. If that line is ever
 * dropped this returns `false` for everyone without `admin`, which shows up
 * as "the hire has no admin nav" — the same symptom as a missing grant. The
 * API carries a spec pinning that line for exactly this reason.
 */
export function hasScope(
	user: IUser | undefined | null,
	scope: AdminScope,
): boolean {
	if (!user) return false;
	if (user.admin) return true;
	return Boolean(user.adminScopes?.includes(scope));
}

/**
 * Does this user hold `scope` AS AN ACTUAL GRANT, ignoring `admin: true`?
 *
 * 🪤 USE THIS, NOT `hasScope`, FOR ANY CONTROL THE API GATES ON A SCOPE ADDED
 * AFTER THE LEGACY-ADMIN BACKFILL. `AdminGuard` was cut over to scopes only, so
 * the boolean grants nothing server-side; migration 1861 papered over that for
 * the 13 scopes that existed when it ran by giving every legacy admin all of
 * them. A scope introduced later needs its own backfill migration to reach
 * anyone — `SUPPORT_ACCESS_OVERRIDE` is the first, delivered to `admin = true`
 * by api migration `1863000000000`, and `STAFF_ACCESS_WRITE` — which gates the
 * whole staff-access grant surface — by `1864000000000`.
 *
 * So `hasScope` would render such a control for any founder whose environment
 * has not run that migration yet, and their very next API call 403s. Showing a
 * switch that cannot work is worse than hiding one that could: the operator
 * concludes the feature is broken rather than that their database is behind.
 * This helper reads only real grants, so the UI matches the API exactly.
 *
 * One-off grants without a deploy: `bun run grant-scope` in equility-api.
 */
export function hasGrantedScope(
	user: IUser | undefined | null,
	scope: AdminScope,
): boolean {
	return Boolean(user?.adminScopes?.includes(scope));
}

/** Does this user hold at least one of `scopes`? */
export function hasAnyScope(
	user: IUser | undefined | null,
	scopes: AdminScope[],
): boolean {
	return scopes.some((scope) => hasScope(user, scope));
}

/**
 * Should the admin console appear at all?
 *
 * Any scope at all is enough — the per-item gating below decides what is
 * inside it.
 */
export function hasAnyAdminAccess(user: IUser | undefined | null): boolean {
	if (!user) return false;
	return Boolean(user.admin) || (user.adminScopes?.length ?? 0) > 0;
}
